import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  makeStyles,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";

import Autocomplete from "@material-ui/lab/Autocomplete";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import VisibilityIcon from "@material-ui/icons/Visibility";
import LocalPrintshopIcon from "@material-ui/icons/LocalPrintshop";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";

import { AuctionListLimit, printLot } from "src/adminModel/auctions";

// =====================================================
// LAYOUT
//
// Two rows of three. Change LOTS_PER_PAGE and the page split, the page
// count and the info box all follow -- nothing else is hardcoded.
//
// If you raise it past 6, shrink IMAGE_HEIGHT_MM too. At 6 per page each
// card has about 107mm of height on Letter paper; the image is the only
// part of the card that can give up space without losing information.
// =====================================================

const LOTS_PER_PAGE = 6;
const COLUMNS = 3;
const IMAGE_HEIGHT_MM = 38;

// The switches, in display order. Rendering them from a list instead of
// eight hand-written <FormControlLabel> blocks means adding a field is
// one line here.
const PRINT_FIELDS = [
  { name: "artist", label: "Artist Name" },
  { name: "title", label: "Title" },
  { name: "medium", label: "Medium" },
  { name: "signAndDate", label: "Signed & Dated" },
  { name: "size", label: "Size" },
  { name: "startingPrice", label: "Starting Price" },
  { name: "hammerPrice", label: "Hammer Price" },
  { name: "reserve", label: "Reserve" },
];

const useStyles = makeStyles((theme) => ({
  root: { width: "100%" },

  card: {
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "none",
    overflow: "hidden",
    backgroundColor: "#fff",
  },

  header: {
    padding: theme.spacing(2.25, 2.5),
    backgroundColor: "#fafafa",
  },

  headerRow: { display: "flex", alignItems: "center" },

  headerIcon: {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eef2ff",
    color: theme.palette.primary.main,
    marginRight: 12,
  },

  title: { fontSize: 16, fontWeight: 600, color: "#1f2937" },

  subtitle: { marginTop: 3, fontSize: 12, color: "#6b7280" },

  content: { padding: theme.spacing(2.5) },

  field: {
    "& .MuiOutlinedInput-root": { borderRadius: 8 },
  },

  sectionBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.75),
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
  },

  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: theme.spacing(1),
  },

  switchGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "4px 8px",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },

  switchLabel: { marginLeft: 0, marginRight: 0 },

  infoBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    backgroundColor: "#fff",
  },

  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    "&:last-child": { marginBottom: 0 },
  },

  infoLabel: { fontSize: 12, color: "#6b7280" },

  infoValue: { fontSize: 12, fontWeight: 600, color: "#111827" },

  actionSection: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(2),
  },

  actionButton: {
    minHeight: 42,
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    paddingLeft: 18,
    paddingRight: 18,
    boxShadow: "none",
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

// =====================================================
// HELPERS
// =====================================================

const escapeHtml = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// Preserves the existing HTML in lot fields (the catalogue stores <i>,
// <br> and similar), only turning plain newlines into line breaks.
const cleanHtml = (value) => {
  if (!value) {
    return "";
  }

  return String(value).replace(/\n/g, "<br />").trim();
};

const formatMoney = (value) => {
  const number = parseFloat(value || 0);

  if (Number.isNaN(number)) {
    return "";
  }

  return number.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const peso = (value) => (value ? `&#8369; ${escapeHtml(formatMoney(value))}` : "");

// =====================================================
// PRINT DOCUMENT
// =====================================================

const renderLot = (lot, fields) => {
  const line = (cls, html) =>
    html
      ? `<div class="${cls} editable" contenteditable="true">${html}</div>`
      : "";

  return `
    <div class="lot-card">
      <div class="lot-image-wrapper">
        ${
          lot.img_url
            ? `<img src="${escapeHtml(lot.img_url)}" alt="Lot"
                    class="lot-image" crossorigin="anonymous" />`
            : `<div class="no-image">No Image</div>`
        }
      </div>

      <div class="lot-information">
        <div class="lot-number editable" contenteditable="true">
          LOT # ${escapeHtml(lot.lot_number || "")}
        </div>

        ${fields.artist ? line("artist", cleanHtml(lot.artist)) : ""}
        ${fields.title ? line("art-title", cleanHtml(lot.title)) : ""}

        <div class="details-list">
          ${fields.signAndDate ? line("detail-line", cleanHtml(lot.signed_dated)) : ""}
          ${fields.medium ? line("detail-line", cleanHtml(lot.MEDIUM)) : ""}
          ${fields.size ? line("detail-line", cleanHtml(lot.size)) : ""}
          ${
            fields.startingPrice
              ? line("price-line", `Starting: ${peso(lot.starting_price)}`)
              : ""
          }
          ${fields.hammerPrice ? line("price-line", `HP: ${peso(lot.hp)}`) : ""}
          ${fields.reserve ? line("price-line", `Reserve: ${peso(lot.reserve)}`) : ""}
        </div>
      </div>
    </div>
  `;
};

const renderPage = (pageLots, pageIndex, totalPages, eventName, consignorName, fields) => `
  <div class="paper">
    <div class="page-header">
      <div>
        <img src="/static/logo140.png" class="logo" alt="Leon Gallery" />
      </div>

      <div class="contact">
        <div><b>www.leon-gallery.com</b></div>
        <div>www.facebook.com/leongallerymakati</div>
        <div>leongallerymakati@gmail.com</div>
        <div>info@leon-gallery.com</div>
      </div>
    </div>

    <div class="document-title editable" contenteditable="true">
      Custom Print
    </div>

    <div class="document-info">
      <div class="info-row">
        <span class="info-label">Event:</span>
        <span class="info-value editable" contenteditable="true">
          ${escapeHtml(eventName)}
        </span>
      </div>

      ${
        consignorName
          ? `
            <div class="info-row">
              <span class="info-label">Name:</span>
              <span class="info-value editable" contenteditable="true">
                ${escapeHtml(consignorName)}
              </span>
            </div>
          `
          : ""
      }
    </div>

    <div class="lot-grid">
      ${pageLots.map((lot) => renderLot(lot, fields)).join("")}
    </div>

    <div class="page-number">
      Page ${pageIndex + 1} of ${totalPages}
    </div>
  </div>
`;

const PRINT_STYLES = `
  @page {
    size: Letter portrait;
    margin: 0;
  }

  * { box-sizing: border-box; }

  html, body {
    margin: 0;
    padding: 0;
    background: #e5e7eb;
    color: #000;
    font-family: Arial, Helvetica, sans-serif;
  }

  body {
    padding-top: 60px;
    padding-bottom: 30px;
  }

  /* ===== TOP BAR ===== */

  .topbar {
    position: fixed;
    top: 0; left: 0; right: 0;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    background: #fff;
    border-bottom: 1px solid #d1d5db;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    z-index: 9999;
  }

  .preview-title {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }

  .print-button {
    border: none;
    background: #1976d2;
    color: #fff;
    padding: 10px 18px;
    border-radius: 7px;
    font: 600 14px Arial, sans-serif;
    cursor: pointer;
  }

  .print-button:hover { background: #1565c0; }

  /* ===== PAPER ===== */

  .paper {
    width: 215.9mm;
    min-height: 279.4mm;
    margin: 20px auto;
    padding: 12mm;
    background: #fff;
    box-shadow: 0 5px 25px rgba(0, 0, 0, 0.15);
    position: relative;
    page-break-after: always;
    break-after: page;
  }

  .paper:last-child {
    page-break-after: auto;
    break-after: auto;
  }

  /* ===== HEADER ===== */

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 8px;
    border-bottom: 1px solid #000;
  }

  .logo { width: 150px; height: auto; }

  .contact {
    text-align: right;
    font-size: 8px;
    line-height: 1.5;
    color: #333;
  }

  .document-title {
    text-align: center;
    font-size: 17px;
    font-weight: 700;
    text-transform: uppercase;
    margin: 14px 0 12px;
  }

  .document-info { margin-bottom: 12px; }

  .info-row {
    display: flex;
    margin-bottom: 4px;
    font-size: 10px;
  }

  .info-label { width: 55px; font-weight: 700; }

  .info-value { flex: 1; min-height: 14px; }

  /* ===== LOT GRID ===== */

  .lot-grid {
    display: grid;
    grid-template-columns: repeat(${COLUMNS}, minmax(0, 1fr));
    gap: 8px;
    align-items: start;
    width: 100%;
  }

  /* ===== LOT CARD ===== */

  .lot-card {
    border: 0.7px solid #b8b8b8;
    border-radius: 3px;
    padding: 7px;
    display: flex;
    flex-direction: column;
    background: #fff;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /*
    ${IMAGE_HEIGHT_MM}mm, down from 54mm when the page held three lots.

    Letter is 279mm. Take off 24mm of padding and ~38mm for the logo,
    title and event lines, and two rows share ~215mm -- about 107mm each.
    The old card was ~105mm: close enough that a title wrapping onto a
    second line pushed it over, and break-inside: avoid then moved the
    whole card to the next page. At ${IMAGE_HEIGHT_MM}mm a card is ~89mm,
    which leaves room for long titles.
  */

  .lot-image-wrapper {
    width: 100%;
    height: ${IMAGE_HEIGHT_MM + 4}mm;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
    border-bottom: 1px solid #eee;
    margin-bottom: 7px;
  }

  .lot-image {
    max-width: 100%;
    max-height: ${IMAGE_HEIGHT_MM}mm;
    object-fit: contain;
    display: block;
  }

  .no-image {
    width: 100%;
    height: ${IMAGE_HEIGHT_MM}mm;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #888;
    font-size: 9px;
    border: 1px dashed #ccc;
  }

  /* ===== LOT INFORMATION ===== */

  .lot-information {
    display: flex;
    flex-direction: column;
  }

  .lot-number {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 5px;
    line-height: 1.1;
  }

  .artist {
    font-size: 10px;
    font-weight: 700;
    line-height: 1.2;
  }

  .art-title {
    font-size: 10px;
    font-style: italic;
    line-height: 1.2;
  }

  .details-list {
    border-top: 1px solid #eee;
    margin-top: 3px;
    padding-top: 4px;
  }

  .detail-line,
  .price-line {
    font-size: 8px;
    line-height: 1.3;
    margin-bottom: 2px;
  }

  /* ===== EDITABLE ===== */

  .editable {
    outline: none;
    cursor: text;
    border-radius: 2px;
  }

  .editable:hover { background: #fffde7; }

  .editable:focus {
    background: #fff8cc;
    box-shadow: inset 0 0 0 1px #d6b656;
  }

  /* ===== PAGE NUMBER ===== */

  .page-number {
    position: absolute;
    bottom: 5mm;
    right: 12mm;
    font-size: 8px;
    color: #777;
  }

  /* ===== LOADING (shown while lots are fetched) ===== */

  .loading {
    font: 14px Arial, sans-serif;
    color: #555;
    text-align: center;
    margin-top: 120px;
  }

  /* ===== PRINT ===== */

  @media print {
    html, body { background: #fff; }

    body { padding: 0; }

    .topbar { display: none !important; }

    .paper {
      margin: 0;
      box-shadow: none;
    }

    .editable:hover,
    .editable:focus {
      background: transparent !important;
      box-shadow: none !important;
    }
  }
`;

const buildPrintDocument = (lots, eventName, consignorName, fields) => {
  const pages = [];

  for (let i = 0; i < lots.length; i += LOTS_PER_PAGE) {
    pages.push(lots.slice(i, i + LOTS_PER_PAGE));
  }

  const pagesHtml = pages
    .map((pageLots, index) =>
      renderPage(pageLots, index, pages.length, eventName, consignorName, fields)
    )
    .join("");

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <title>${escapeHtml(eventName || "Custom Print")}</title>
        <style>${PRINT_STYLES}</style>
      </head>
      <body>
        <div class="topbar">
          <div class="preview-title">Custom Print Preview</div>
          <button class="print-button" onclick="window.print()">Print</button>
        </div>
        ${pagesHtml}
      </body>
    </html>
  `;
};

// =====================================================
// COMPONENT
// =====================================================

const Views = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [geteventList, setEventList] = useState([]);
  const [lotNumberData, setLotNumber] = useState("");
  const [eventId, setEventId] = useState("");
  const [eventName, setEventName] = useState("");
  const [consignorName, setConsignorName] = useState("");
  const [data1, setData1] = useState([]);

  const [state, setState] = useState({
    artist: true,
    title: true,
    signAndDate: true,
    medium: true,
    size: true,
    startingPrice: true,
    hammerPrice: true,
    reserve: true,
  });

  // ===================================================
  // LOAD EVENTS
  // ===================================================

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const results = await AuctionListLimit();

        setEventList(results && results.data ? results.data : []);
      } catch (error) {
        console.error("Auction list error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ===================================================
  // FETCH LOTS
  // ===================================================

  const FinLot = async (selectedEventId, lots) => {
    if (!selectedEventId || !lots || !lots.trim()) {
      setData1([]);
      return [];
    }

    try {
      setLoading(true);

      const lotNumbers = lots.replace(/\n/g, ",").replace(/,\s*$/, "");

      const results = await printLot(lotNumbers, selectedEventId);

      if (!Array.isArray(results)) {
        setData1([]);
        return [];
      }

      const sorted = [...results].sort(
        (a, b) => Number(a.lot_number) - Number(b.lot_number)
      );

      setData1(sorted);

      return sorted;
    } catch (error) {
      console.error("Lot fetch error:", error);
      setData1([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // ===================================================
  // SELECT EVENT
  // ===================================================

  const handlepick = async (event, value) => {
    if (!value) {
      setEventName("");
      setEventId("");
      setData1([]);
      return;
    }

    setEventName(value.auction_name || "");
    setEventId(value.id || "");

    if (lotNumberData.trim()) {
      await FinLot(value.id, lotNumberData);
    }
  };

  // ===================================================
  // LOT NUMBER CHANGE
  // ===================================================

  const handleLotNumberChange = async (event) => {
    const { value } = event.target;

    setLotNumber(value);

    if (eventId && value.trim()) {
      await FinLot(eventId, value);
    } else {
      setData1([]);
    }
  };

  const handleSwitchChange = (event) => {
    const { name, checked } = event.target;
    setState((prev) => ({ ...prev, [name]: checked }));
  };

  // ===================================================
  // OPEN PRINT PREVIEW
  // ===================================================

  const handlePrintPreview = async () => {
    if (!eventId) {
      alert("Please select an Event / Auction.");
      return;
    }

    if (!lotNumberData.trim()) {
      alert("Please enter Lot Numbers.");
      return;
    }

    // Open the window FIRST, before any await.
    //
    // Browsers only allow window.open() as a direct result of a click.
    // The old code awaited FinLot() and THEN opened the window -- by then
    // the click was no longer "current", so Chrome and Safari treated it
    // as an unrequested pop-up and blocked it. That is why staff kept
    // seeing "Please allow pop-ups". Opening immediately keeps the click,
    // and we fill the window in once the data arrives.
    const printWindow = window.open("", "_blank", "width=1200,height=900");

    if (!printWindow) {
      alert("Please allow pop-ups for this website.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(
      `<html><head><title>Loading...</title>
         <style>${PRINT_STYLES}</style></head>
       <body><div class="loading">Loading lots...</div></body></html>`
    );
    printWindow.document.close();

    // Refresh from the server so the print reflects any edits made since
    // the lots were first loaded.
    const lots = await FinLot(eventId, lotNumberData);

    if (!lots || lots.length === 0) {
      printWindow.close();
      alert("No lot records found.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(
      buildPrintDocument(lots, eventName, consignorName, state)
    );
    printWindow.document.close();
    printWindow.focus();
  };

  // ===================================================
  // LOT COUNT
  // ===================================================

  const lotCount = lotNumberData
    .split(/[\n,\s]+/)
    .filter((item) => item.trim()).length;

  const pageCount = Math.ceil(lotCount / LOTS_PER_PAGE);

  // ===================================================
  // RENDER
  // ===================================================

  return (
    <Box className={classes.root}>
      <Grid container spacing={3}>
        {/* ========== LOT NUMBERS ========== */}

        <Grid item xs={12} md={5}>
          <Card className={classes.card}>
            <Box className={classes.header}>
              <Box className={classes.headerRow}>
                <Box className={classes.headerIcon}>
                  <FormatListNumberedIcon />
                </Box>

                <Box>
                  <Typography className={classes.title}>
                    Lot Selection
                  </Typography>

                  <Typography className={classes.subtitle}>
                    Enter the lot numbers you want to include
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider />

            <CardContent className={classes.content}>
              <TextField
                fullWidth
                multiline
                rows={12}
                variant="outlined"
                label="Lot Numbers"
                placeholder={"Example:\n1\n2\n3\n4"}
                value={lotNumberData}
                onChange={handleLotNumberChange}
                className={classes.field}
              />

              <Box className={classes.infoBox}>
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>
                    Total Lots
                  </Typography>
                  <Typography className={classes.infoValue}>
                    {lotCount}
                  </Typography>
                </Box>

                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>
                    Lots per Page
                  </Typography>
                  <Typography className={classes.infoValue}>
                    {LOTS_PER_PAGE}
                  </Typography>
                </Box>

                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>Pages</Typography>
                  <Typography className={classes.infoValue}>
                    {pageCount}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* ========== PRINT SETTINGS ========== */}

        <Grid item xs={12} md={7}>
          <Card className={classes.card}>
            <Box className={classes.header}>
              <Box className={classes.headerRow}>
                <Box className={classes.headerIcon}>
                  <LocalPrintshopIcon />
                </Box>

                <Box>
                  <Typography className={classes.title}>
                    Custom Print
                  </Typography>

                  <Typography className={classes.subtitle}>
                    Configure the information shown on the printed page
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider />

            <CardContent className={classes.content}>
              <Autocomplete
                options={geteventList}
                value={
                  geteventList.find(
                    (item) => String(item.id) === String(eventId)
                  ) || null
                }
                getOptionLabel={(option) =>
                  option ? option.auction_name || "" : ""
                }
                onChange={handlepick}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Event / Auction"
                    variant="outlined"
                    className={classes.field}
                  />
                )}
              />

              <Box mt={1}>
                <TextField
                  fullWidth
                  label="Name / Consignor"
                  value={consignorName}
                  onChange={(e) => setConsignorName(e.target.value)}
                  variant="outlined"
                  className={classes.field}
                  placeholder="Enter name"
                />
              </Box>

              <Box className={classes.sectionBox}>
                <Typography className={classes.sectionTitle}>
                  Information to Display
                </Typography>

                <FormControl component="fieldset" fullWidth>
                  <FormLabel
                    component="legend"
                    style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}
                  >
                    Select the fields that should appear on the printout.
                  </FormLabel>

                  <FormGroup className={classes.switchGrid}>
                    {PRINT_FIELDS.map((field) => (
                      <FormControlLabel
                        key={field.name}
                        className={classes.switchLabel}
                        control={
                          <Switch
                            checked={state[field.name]}
                            onChange={handleSwitchChange}
                            name={field.name}
                            color="primary"
                          />
                        }
                        label={field.label}
                      />
                    ))}
                  </FormGroup>

                  <FormHelperText>
                    Only selected information will be displayed.
                  </FormHelperText>
                </FormControl>
              </Box>
            </CardContent>

            <Divider />

            <Box className={classes.actionSection}>
              <Button
                variant="contained"
                color="primary"
                className={classes.actionButton}
                startIcon={<VisibilityIcon />}
                onClick={handlePrintPreview}
                disabled={!eventId || !lotNumberData.trim()}
              >
                Preview &amp; Print
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default Views;